import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty dependency array is correct here - we only want to set up the listener once

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Wealth Management', path: '/asset-management' },
    { name: 'Tax', path: '/tax' },
    { name: 'Estate', path: '/estate' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top bar with contact info */}
      <div className="bg-[#0a69b2] text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:941-545-9057" className="flex items-center gap-2 hover:text-gray-200 transition-colors">
              <Phone size={14} />
              <span>941-545-9057</span>
            </a>
            <a href="mailto:contact@paracca.com" className="flex items-center gap-2 hover:text-gray-200 transition-colors">
              <Mail size={14} />
              <span>contact@paracca.com</span>
            </a>
          </div>
          <div className="hidden md:block text-xs">
            FINRA Registered Investment Advisory Firm
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <header
        className={`sticky top-0 z-50 bg-white transition-all duration-300 ${
          isScrolled ? 'shadow-md' : 'shadow-sm'
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img 
                src="/logo-blue.png" 
                alt="Paracca Financial Group" 
                className="h-16 sm:h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-montserrat text-sm font-medium transition-colors relative group ${
                    isActive(link.path)
                      ? 'text-[#0a69b2]'
                      : 'text-gray-600 hover:text-[#0a69b2]'
                  }`}
                >
                  {link.name}
                  <span
                    className={`absolute -bottom-1 left-0 w-full h-0.5 bg-[#0a69b2] transition-transform origin-left ${
                      isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                    }`}
                  />
                </Link>
              ))}
              <a
                href="https://client.myadvisorlink.com/auth/users/webportal/paracca"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-[#0a69b2] hover:bg-[#0858a0] text-white font-montserrat font-semibold px-6 transition-all duration-300 hover:shadow-lg">
                  Client Login
                </Button>
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-gray-600 hover:text-[#0a69b2] transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block py-2 font-montserrat text-sm font-medium transition-colors ${
                    isActive(link.path)
                      ? 'text-[#0a69b2]'
                      : 'text-gray-600 hover:text-[#0a69b2]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <a
                href="https://client.myadvisorlink.com/auth/users/webportal/paracca"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Button className="w-full bg-[#0a69b2] hover:bg-[#0858a0] text-white font-montserrat font-semibold">
                  Client Login
                </Button>
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
